export const REGISTER_PREFERENCES_STORAGE_KEY = "vocabularium_register_preferences";
export const REGISTER_RECENCY_PREFERENCES_STORAGE_KEY =
  "vocabularium_register_recency_preferences";

export const REGISTER_SEARCH_MODES = {
  STARTS_WITH: "startsWith",
  CONTAINS: "contains",
};

export const REGISTER_LIST_DISPLAY_MODES = {
  LAST_RESULT: "lastResult",
  GRADATION: "gradation",
  PAQUET: "paquet",
  RATIO: "ratio",
  STATS: "stats",
  RECENCY_DAYS: "recencyDays",
  GAME_COUNT: "gameCount",
  CAPTURE_DATE: "captureDate",
};

// Positionnement automatique de la barre de scroll du volet détail (à droite),
// appliqué à tous les mots. "free" = aucun positionnement (scroll libre).
export const REGISTER_STATS_SCROLL_ANCHORS = {
  FREE: "free",
  STATS_TABLE: "statsTable",
};

export const REGISTER_EDIT_SCROLL_ANCHORS = {
  FREE: "free",
  DEFINITION: "definition",
  EXTRAIT: "extrait",
};

export const DEFAULT_REGISTER_PREFERENCES = {
  searchMode: REGISTER_SEARCH_MODES.CONTAINS,
  listDisplayMode: REGISTER_LIST_DISPLAY_MODES.PAQUET,
  showColorCode: true,
  statsScrollAnchor: REGISTER_STATS_SCROLL_ANCHORS.FREE,
  editScrollAnchor: REGISTER_EDIT_SCROLL_ANCHORS.FREE,
};

export const DEFAULT_REGISTER_RECENCY_PREFERENCES = {
  searchMode: DEFAULT_REGISTER_PREFERENCES.searchMode,
  listDisplayMode: DEFAULT_REGISTER_PREFERENCES.listDisplayMode,
  showColorCode: DEFAULT_REGISTER_PREFERENCES.showColorCode,
};

export const REGISTER_STATS_SCROLL_ANCHOR_LABELS = {
  [REGISTER_STATS_SCROLL_ANCHORS.FREE]: "Scroll libre",
  [REGISTER_STATS_SCROLL_ANCHORS.STATS_TABLE]: "Tableau des statistiques",
};

export const REGISTER_EDIT_SCROLL_ANCHOR_LABELS = {
  [REGISTER_EDIT_SCROLL_ANCHORS.FREE]: "Scroll libre",
  [REGISTER_EDIT_SCROLL_ANCHORS.DEFINITION]: "Définition",
  [REGISTER_EDIT_SCROLL_ANCHORS.EXTRAIT]: "Extrait de texte",
};

export const REGISTER_SEARCH_MODE_LABELS = {
  [REGISTER_SEARCH_MODES.STARTS_WITH]: "Commence par",
  [REGISTER_SEARCH_MODES.CONTAINS]: "Contenant",
};

export const REGISTER_LIST_DISPLAY_LABELS = {
  [REGISTER_LIST_DISPLAY_MODES.LAST_RESULT]: "Dernière parution",
  [REGISTER_LIST_DISPLAY_MODES.GRADATION]: "Gradation",
  [REGISTER_LIST_DISPLAY_MODES.PAQUET]: "Paquet",
  [REGISTER_LIST_DISPLAY_MODES.RATIO]: "Taux de réussite",
  [REGISTER_LIST_DISPLAY_MODES.STATS]: "Jeux / réussites / échecs",
  [REGISTER_LIST_DISPLAY_MODES.RECENCY_DAYS]: "Jours depuis le jeu",
  [REGISTER_LIST_DISPLAY_MODES.GAME_COUNT]: "Nbr. de fois joués",
  [REGISTER_LIST_DISPLAY_MODES.CAPTURE_DATE]: "Date de capture",
};

export const REGISTER_SEARCH_MODE_HELP = {
  [REGISTER_SEARCH_MODES.STARTS_WITH]:
    "Seuls les mots commençant par votre saisie apparaissent pendant la frappe.",
  [REGISTER_SEARCH_MODES.CONTAINS]:
    "Les mots contenant votre saisie apparaissent, quelle que soit sa position.",
};

export const REGISTER_LIST_DISPLAY_HELP = {
  [REGISTER_LIST_DISPLAY_MODES.LAST_RESULT]:
    "Succès ou échec lors de la dernière apparition du mot en jeu.",
  [REGISTER_LIST_DISPLAY_MODES.GRADATION]:
    "Gradation EWSS actuelle, avec un code couleur selon la maîtrise.",
  [REGISTER_LIST_DISPLAY_MODES.PAQUET]:
    "Strate du mot : Noyau, Paquet 1–4, ou Paquet 5.",
  [REGISTER_LIST_DISPLAY_MODES.RATIO]:
    "Proportion de restitutions correctes sur toutes les parties jouées.",
  [REGISTER_LIST_DISPLAY_MODES.STATS]:
    "Nombre total de parties, de réussites et d'échecs cumulés.",
  [REGISTER_LIST_DISPLAY_MODES.RECENCY_DAYS]:
    "Nombre de jours écoulés depuis la dernière apparition du mot en jeu.",
  [REGISTER_LIST_DISPLAY_MODES.GAME_COUNT]:
    "Nombre de fois où le mot a été joué, avec le même code couleur que les strates de parution.",
  [REGISTER_LIST_DISPLAY_MODES.CAPTURE_DATE]:
    "Date à laquelle le mot a été ajouté au registre, au format JJ/MM/AAAA.",
};

const VALID_SEARCH_MODES = new Set(Object.values(REGISTER_SEARCH_MODES));
const VALID_LIST_DISPLAY_MODES = new Set(Object.values(REGISTER_LIST_DISPLAY_MODES));
const VALID_STATS_SCROLL_ANCHORS = new Set(Object.values(REGISTER_STATS_SCROLL_ANCHORS));
const VALID_EDIT_SCROLL_ANCHORS = new Set(Object.values(REGISTER_EDIT_SCROLL_ANCHORS));

export function sanitizeRegisterPreferences(raw) {
  if (!raw || typeof raw !== "object") {
    return { ...DEFAULT_REGISTER_PREFERENCES };
  }

  return {
    searchMode: VALID_SEARCH_MODES.has(raw.searchMode)
      ? raw.searchMode
      : DEFAULT_REGISTER_PREFERENCES.searchMode,
    listDisplayMode: VALID_LIST_DISPLAY_MODES.has(raw.listDisplayMode)
      ? raw.listDisplayMode
      : DEFAULT_REGISTER_PREFERENCES.listDisplayMode,
    showColorCode:
      typeof raw.showColorCode === "boolean"
        ? raw.showColorCode
        : DEFAULT_REGISTER_PREFERENCES.showColorCode,
    statsScrollAnchor: VALID_STATS_SCROLL_ANCHORS.has(raw.statsScrollAnchor)
      ? raw.statsScrollAnchor
      : DEFAULT_REGISTER_PREFERENCES.statsScrollAnchor,
    editScrollAnchor: VALID_EDIT_SCROLL_ANCHORS.has(raw.editScrollAnchor)
      ? raw.editScrollAnchor
      : DEFAULT_REGISTER_PREFERENCES.editScrollAnchor,
  };
}

export function readRegisterPreferences() {
  try {
    const stored = localStorage.getItem(REGISTER_PREFERENCES_STORAGE_KEY);
    if (!stored) return { ...DEFAULT_REGISTER_PREFERENCES };
    return sanitizeRegisterPreferences(JSON.parse(stored));
  } catch {
    return { ...DEFAULT_REGISTER_PREFERENCES };
  }
}

export function writeRegisterPreferences(preferences) {
  try {
    localStorage.setItem(
      REGISTER_PREFERENCES_STORAGE_KEY,
      JSON.stringify(sanitizeRegisterPreferences(preferences))
    );
  } catch {
    // ignore quota errors
  }
}

export function sanitizeRegisterRecencyPreferences(raw) {
  const sanitized = sanitizeRegisterPreferences(raw);
  return {
    searchMode: sanitized.searchMode,
    listDisplayMode: sanitized.listDisplayMode,
    showColorCode: sanitized.showColorCode,
  };
}

export function readRegisterRecencyPreferences() {
  try {
    const stored = localStorage.getItem(REGISTER_RECENCY_PREFERENCES_STORAGE_KEY);
    if (!stored) return { ...DEFAULT_REGISTER_RECENCY_PREFERENCES };
    return sanitizeRegisterRecencyPreferences(JSON.parse(stored));
  } catch {
    return { ...DEFAULT_REGISTER_RECENCY_PREFERENCES };
  }
}

export function writeRegisterRecencyPreferences(preferences) {
  try {
    localStorage.setItem(
      REGISTER_RECENCY_PREFERENCES_STORAGE_KEY,
      JSON.stringify(sanitizeRegisterRecencyPreferences(preferences))
    );
  } catch {
    // ignore quota errors
  }
}
